/**
 * The Graph follows the toolbar project (project scope card 6.2) instead of a
 * project filter of its own: one scope for every panel.
 *
 *   All projects   the whole graph
 *   a project      the project and what it contains (projectSubgraph)
 *   No project     everything that is not a project and not in one
 *   pending        (the active project not resolved yet) nothing, like Boards
 *
 * The page's project dropdown and Projects → "Open in graph" both write the
 * shared scope (setActiveProject), so the title bar and the Graph never
 * disagree.
 */
import { ALL_PROJECTS, NO_PROJECT, normalizeScopeId } from '../scope/projectScopeFilter';
import { setActiveProject, useProjectScopeStore } from '../scope/projectScopeStore';

/** { mode: 'all' | 'project' | 'none' | 'pending', projectId } from the shared scope. */
export function graphProjectMode({ activeProjectId, activeProject, status } = {}) {
  const id = normalizeScopeId(activeProjectId);
  if (id === ALL_PROJECTS) return { mode: 'all', projectId: null };
  if (id === NO_PROJECT) return { mode: 'none', projectId: null };
  if (status !== 'ready' || !activeProject || String(activeProject.id) !== id) return { mode: 'pending', projectId: id };
  return { mode: 'project', projectId: id };
}

/** Drops project nodes and everything a project `contains`, with their edges. */
export function withoutProjects(nodes = [], edges = []) {
  const projectIds = new Set(nodes.filter((n) => n.type === 'project').map((n) => n.id));
  const contained = new Set(edges.filter((e) => e.rel === 'contains' && projectIds.has(e.from)).map((e) => e.to));
  const kept = nodes.filter((n) => !projectIds.has(n.id) && !contained.has(n.id));
  const keep = new Set(kept.map((n) => n.id));
  return { nodes: kept, edges: edges.filter((e) => keep.has(e.from) && keep.has(e.to)) };
}

/** The picker's value: '' = All projects, NO_PROJECT, or a project id. */
export const graphPickerValue = (activeProjectId) => {
  const id = normalizeScopeId(activeProjectId);
  return id === ALL_PROJECTS ? '' : id;
};

/** Picking in the Graph (or "Open in graph") moves the shared scope. */
export const setGraphProject = (value) => setActiveProject(value || ALL_PROJECTS);

export function useGraphProjectScope() {
  const activeProjectId = useProjectScopeStore((s) => s.activeProjectId);
  const activeProject = useProjectScopeStore((s) => s.activeProject);
  const status = useProjectScopeStore((s) => s.status);
  return { activeProjectId, ...graphProjectMode({ activeProjectId, activeProject, status }) };
}
